// src/components/User/ClaimVendorConfirmationPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api';
import { useNotification } from '../../context/NotificationContext';
import Cookies from 'js-cookie';
import Loader from '../Loader';
import SignUpVideo from './SignUpVideo';
import ToggleBillingPeriodNew from '../../components/Form/Input/ToggleBillingPeriodNew';
import SmsConsentCheckbox from '../Form/SmsConsentCheckbox';

// Стили скопированы из DisplayPromoAttorneyInfo для идентичного вида
const styles = {
    pageContainer: {
        display: 'flex',
        height: '80vh',
        width: '100%',
        overflow: 'hidden',
    },
    videoWrapper: {
        flex: '0 0 50%',
        position: 'relative',
        height: '80vh',
        overflow: 'hidden',
    },
    contentContainer: {
        flex: 1,
        height: '80vh',
        backgroundColor: '#f4f7f9',
        padding: '40px',
        overflowY: 'auto',
        boxSizing: 'border-box',
    },
    infoBlock: {
        padding: '30px 40px',
        maxWidth: '700px',
        width: '100%',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 6px 18px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
    },
    heading: {
        borderBottom: '1px solid #eee',
        paddingBottom: '15px',
        marginBottom: '25px',
        color: '#333',
        fontSize: '24px',
        fontWeight: '600',
        textAlign: 'center',
    },
    subHeading: {
        fontSize: '18px',
        color: '#555',
        marginBottom: '20px',
        textAlign: 'center',
    },
    infoSection: {
        marginBottom: '25px',
        paddingBottom: '20px',
        borderBottom: '1px solid #f0f0f0',
    },
    infoSectionTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#444',
        marginBottom: '15px',
    },
    infoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '12px',
        fontSize: '16px',
        flexWrap: 'wrap',
    },
    infoLabel: {
        fontWeight: 'bold',
        color: '#444',
        marginRight: '10px',
        marginBottom: '5px',
    },
    infoValue: {
        color: '#666',
        textAlign: 'right',
        wordBreak: 'break-word',
        marginLeft: 'auto',
    },
    addressValue: {
        color: '#666',
        textAlign: 'left',
        fontSize: '16px',
        wordBreak: 'break-word',
        marginTop: '5px',
        display: 'block',
        width: '100%',
    },
    trialNotification: {
        marginTop: '15px',
        padding: '12px',
        border: '1px solid #0d6efd',
        borderRadius: '5px',
        backgroundColor: '#cfe2ff',
        fontSize: '1.1em',
        color: '#0a58ca',
        lineHeight: '1.4',
        textAlign: 'center',
    },
    termsText: {
        fontSize: '14px',
        color: '#666',
        marginTop: '20px',
        textAlign: 'center',
        lineHeight: '1.5',
    },
    termsLink: {
        color: '#007bff',
        textDecoration: 'none',
    },
    errorBox: {
        padding: '20px',
        borderRadius: '5px',
        marginBottom: '20px',
        textAlign: 'center',
        backgroundColor: '#fff2f2',
        border: '1px solid #ffcccc',
        color: '#cc0000',
    },
    submissionErrorBox: {
        padding: '15px',
        borderRadius: '5px',
        marginBottom: '15px',
        textAlign: 'center',
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        color: '#721c24',
    },
    buttonContainer: {
        marginTop: '30px',
        textAlign: 'center',
    },
    proceedButton: {
        padding: '12px 25px',
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#fff',
        backgroundColor: '#007bff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        transition: 'background-color 0.3s ease',
    },
};

const ClaimVendorConfirmationPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submissionError, setSubmissionError] = useState(null);
    const [vendorData, setVendorData] = useState(null);
    const [planLoading, setPlanLoading] = useState(true);
    const [planData, setPlanData] = useState(null);
    const [isProceeding, setIsProceeding] = useState(false);
    const [billingPeriod, setBillingPeriod] = useState('monthly');
    const [smsNotificationsConsent, setSmsNotificationsConsent] = useState(false);
    const [smsMarketingConsent, setSmsMarketingConsent] = useState(false);

    const getRequestConfig = useCallback(() => {
        const csrfToken = Cookies.get('csrftoken');
        const headers = { 'Content-Type': 'application/json' };
        if (csrfToken) {
            headers['X-CSRFToken'] = csrfToken;
        } else {
            showNotification("Error", "A required security token is missing. Please refresh the page.", null, "error");
            return null;
        }
        return { headers, withCredentials: true };
    }, [showNotification]);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);
        setPlanLoading(true);

        const fetchData = async () => {
            if (!token) {
                if (isMounted) setError("Token not found in URL.");
                setLoading(false);
                setPlanLoading(false);
                return;
            }

            try {
                // Шаг 1: Загрузка данных вендора по токену
                const vendorResponse = await api.get(`/vendors-app/claim/info/${token}/`);
                const fetchedVendorData = vendorResponse.data;
                if (!isMounted) return;

                setVendorData(fetchedVendorData);
                const serviceTypeId = fetchedVendorData?.service_type?.service_type_id;

                if (!serviceTypeId) {
                    throw new Error("Service Type ID not found in vendor data.");
                }

                // Шаг 2: Загрузка тарифа на основе типа услуги вендора
                const planResponse = await api.get(`/tariff-services/?service_type_id=${serviceTypeId}`);
                if (!isMounted) return;

                const activePlan = (planResponse.data || []).find(p => p.status === 'active');
                if (activePlan) {
                    setPlanData(activePlan);
                } else {
                    throw new Error("No active subscription plan found for this vendor's service type.");
                }

            } catch (err) {
                const errorMsg = err.response?.data?.detail || err.message || "Could not retrieve required information.";
                if (isMounted) setError(errorMsg);
            } finally {
                if (isMounted) {
                    setLoading(false);
                    setPlanLoading(false);
                }
            }
        };

        fetchData();
        return () => { isMounted = false; };
    }, [token, showNotification]);

    const formatAddress = useCallback((addr) => {
        if (!addr) return 'N/A';
        const parts = [addr.street_address, addr.city?.name, addr.state?.name, addr.zip_code?.zip_code].filter(Boolean);
        return parts.join(', ') || 'N/A';
    }, []);

    const handleProceed = useCallback(async () => {
        if (!vendorData || !planData || !token) {
            showNotification("Error", "Cannot proceed. Missing required data.", null, "error");
            return;
        }
        setIsProceeding(true);
        setSubmissionError(null);

        const config = getRequestConfig();
        if (!config) {
            setIsProceeding(false);
            return;
        }

        const payload = {
            promo_token: token,
            billing_period: billingPeriod,
            vendor_id: vendorData.vendor_id,
            sms_notifications_consent: smsNotificationsConsent,
            sms_marketing_consent: smsMarketingConsent,
        };

        try {
            const response = await api.post('/vendors-app/promo/create-checkout-session/', payload, config);
            if (response.data?.checkout_url) {
                window.location.href = response.data.checkout_url;
            } else {
                throw new Error("Checkout URL not received from server.");
            }
        } catch (err) {
            const errorMsg = err.response?.data?.detail || "An unexpected error occurred while initiating activation.";
            setSubmissionError(errorMsg);
            showNotification('Error', errorMsg, null, 'error');
            setIsProceeding(false);
        }
    }, [token, billingPeriod, vendorData, planData, smsNotificationsConsent, smsMarketingConsent, getRequestConfig, showNotification]);

    if (loading) {
        return <Loader />;
    }

    const canProceed = !!vendorData && !!planData && !isProceeding;
    const buttonText = isProceeding ? 'Processing...' : 'Start Trial: $1 for 90 Days';

    return (
        <div style={styles.pageContainer}>
            <div style={styles.videoWrapper}>
                <SignUpVideo style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={styles.contentContainer}>
                <div style={styles.infoBlock}>
                    <h1 style={styles.heading}>Vendor Promotion Activation</h1>
                    {error && !vendorData && (
                        <div style={styles.errorBox}>
                            <h2 style={{ marginTop: 0, color: '#cc0000', fontSize: '18px' }}>Unable to Retrieve Information</h2>
                            <p><strong>{error}</strong></p>
                            <p>This link may be invalid or expired. Please check the link or <Link to="/signup" style={styles.termsLink}>sign up</Link>.</p>
                        </div>
                    )}
                    {submissionError && <div style={styles.submissionErrorBox}><p style={{ margin: 0 }}><strong>Activation Error:</strong> {submissionError}</p></div>}

                    {vendorData ? (
                        <>
                            <p style={styles.subHeading}>Review your details and choose your billing cycle to start the Pro plan trial.</p>

                            <div style={styles.infoSection}>
                                <h2 style={styles.infoSectionTitle}>Vendor Information</h2>
                                <div style={styles.infoRow}><span style={styles.infoLabel}>Vendor Name:</span><span style={styles.infoValue}>{vendorData.name || 'N/A'}</span></div>
                                <div style={styles.infoRow}><span style={styles.infoLabel}>Email:</span><span style={styles.infoValue}>{vendorData.email || 'N/A'}</span></div>
                                <div style={styles.infoRow}><span style={styles.infoLabel}>Phone:</span><span style={styles.infoValue}>{vendorData.phone_number || 'N/A'}</span></div>
                                {vendorData.foundation_year && <div style={styles.infoRow}><span style={styles.infoLabel}>Founded:</span><span style={styles.infoValue}>{vendorData.foundation_year}</span></div>}
                            </div>

                            <div style={styles.infoSection}>
                                <h2 style={styles.infoSectionTitle}>Location & Services</h2>
                                <div style={styles.infoRow}><span style={styles.infoLabel}>Address:</span></div>
                                <span style={styles.addressValue}>{formatAddress(vendorData.address)}</span>
                                {vendorData.service_type && <div style={{ ...styles.infoRow, marginTop: '15px' }}><span style={styles.infoLabel}>Service Type:</span><span style={styles.infoValue}>{vendorData.service_type.service_type_name}</span></div>}
                            </div>

                            <div style={styles.subscriptionSection}>
                                <h2 style={styles.infoSectionTitle}>Pro Plan Trial Activation</h2>
                                {planLoading ? (
                                    <div style={{ textAlign: 'center', color: '#888', padding: '10px 0' }}><Loader size="small" message="Loading plan details..." /></div>
                                ) : planData ? (
                                    <>
                                        <ToggleBillingPeriodNew
                                            billingPeriod={billingPeriod}
                                            onBillingPeriodChange={setBillingPeriod}
                                            disabled={isProceeding || !canProceed}
                                            monthlyPrice={planData.monthly_price}
                                            annualPrice={planData.annual_price}
                                            effectiveMonthlyPrice={planData.annual_price ? (parseFloat(planData.annual_price) / 12).toFixed(0) : null}
                                        />
                                        <div style={styles.trialNotification}>
                                            <strong>Important:</strong> You will be charged <strong>$1.00 today</strong> for a <strong>90-day trial</strong> of the Pro Plan. After the trial, your selected plan ({billingPeriod === 'monthly' ? `$${planData.monthly_price}/month` : `$${planData.annual_price}/year, billed annually`}) will begin automatically unless cancelled.
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ ...styles.errorBox, backgroundColor: '#fffbe6', borderColor: '#ffe58f', color: '#664d03', marginBottom: '15px' }}>
                                        Subscription plan details could not be loaded for this vendor's service type. Activation cannot proceed.
                                    </div>
                                )}
                            </div>

                            <div style={styles.termsText}>
                                By clicking "{buttonText}", you agree to charge $1.00 for the trial and to our <Link to="/terms-of-use" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>Terms of Use</Link> and <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>Privacy Policy</Link>.
                            </div>

                            <SmsConsentCheckbox
                                checked={smsNotificationsConsent}
                                onChange={(e) => setSmsNotificationsConsent(e.target.checked)}
                                style={{marginTop: '15px'}}
                            />
                            <SmsConsentCheckbox
                                name="smsMarketingConsent"
                                checked={smsMarketingConsent}
                                onChange={(e) => setSmsMarketingConsent(e.target.checked)}
                                style={{marginTop: '15px'}}
                                variant="marketing"
                            />

                            <div style={styles.buttonContainer}>
                                <button style={{ ...styles.proceedButton, ...(!canProceed ? { backgroundColor: '#cccccc', cursor: 'not-allowed', opacity: 0.7 } : {}) }} onClick={handleProceed} disabled={!canProceed}>
                                    {buttonText}
                                </button>
                                <p style={{ ...styles.termsText, fontSize: '12px', marginTop: '15px' }}>
                                    Request profile removal: email <a href="mailto:law@law.law" style={styles.termsLink}>law@law.law</a>.
                                </p>
                            </div>
                        </>
                    ) : (
                        !loading && !error && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                No information is available for this activation link.
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClaimVendorConfirmationPage;
